import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { canManagePayments, formatAdminRole, getUserRole, isDebtAssignedToUser, isLoanOfficer } from "../accessControl";
import { formatMoney, getInlineLoader } from "../Helpers";
import { FaUsers } from "react-icons/fa";
import {
  MdArrowOutward,
  MdCalendarToday,
  MdDirectionsBike,
  MdOutlinePayments,
  MdReceiptLong,
} from "react-icons/md";
import { RiSecurePaymentLine } from "react-icons/ri";

export default function View(props) {
  const appContext = useContext(AppContext);

  const [ready, setReady] = useState(false);

  async function init() {
    setReady(false);

    await Promise.all([
      appContext.getAllCustomers(),
      appContext.getAllMotorcycles(),
      appContext.getAllSales(),
      appContext.getAllDebts(),
      appContext.getAllPayments(),
    ]);

    setReady(true);
  }

  const getDayKey = (value) => {
    if (!value && value !== 0) return "";

    let date = null;

    if (typeof value === "number" || /^\d+$/.test(String(value))) {
      const numericValue = Number(value);
      date = new Date(String(numericValue).length <= 10 ? numericValue * 1000 : numericValue);
    } else {
      date = new Date(value);
    }

    if (Number.isNaN(date?.getTime?.())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const getTodayKey = () => getDayKey(Date.now());

  useEffect(() => {
    init();
  }, []);

  if (ready) {
    const currentUser = appContext.currentUserData;
    const currentRole = getUserRole(currentUser);
    const customers = appContext.allCustomers || [];
    const motorcycles = appContext.allMotorcycles || [];
    const sales = appContext.allSales || [];
    const debts = appContext.allDebts || [];
    const payments = appContext.allPayments || [];
    const assignedDebts = debts.filter((item) => isDebtAssignedToUser(item, currentUser));
    const dashboardDebts = isLoanOfficer(currentUser) ? assignedDebts : debts;

    const todayKey = getTodayKey();
    const activeDebts = dashboardDebts.filter((item) => item.status === "active");
    const publishedMotorcycles = motorcycles.filter((item) => item.status === "published");

    const totalDebtAmount = activeDebts.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0);
    const totalDebtBalance = activeDebts.reduce((sum, item) => sum + Number(item.balance || 0), 0);
    const totalPaidAcrossDebts = activeDebts.reduce((sum, item) => sum + Number(item.amountPaid || 0), 0);
    const totalPayments = payments.reduce((sum, item) => sum + Number(item.amount || 0), 0);

    const todaysDueDebts = activeDebts.filter((item) => getDayKey(item.dueDate) === todayKey);
    const todaysPayments = payments.filter((item) => getDayKey(item.dateCreated || item.date) === todayKey);
    const todaysSales = sales.filter((item) => getDayKey(item.dateCreated || item.date) === todayKey);

    const todayDueDebtTotal = todaysDueDebts.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0);
    const todayCollectedTotal = todaysPayments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const todayOutstandingBalance = todaysDueDebts.reduce((sum, item) => sum + Number(item.balance || 0), 0);

    const todayLabel = new Date().toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    const summaryCardsByRole = {
      admin: [
        {
          label: "Customers",
          value: customers.length,
          hint: "Customer profiles on file",
          icon: <FaUsers />,
          navItem: "customers",
        },
        {
          label: "Motorcycles",
          value: motorcycles.length,
          hint: `Inventory management`,
          icon: <MdDirectionsBike />,
          navItem: "motorcycles",
        },
        {
          label: "Active Debts",
          value: activeDebts.length,
          hint: `${formatMoney(totalDebtBalance)} outstanding balance`,
          icon: <RiSecurePaymentLine />,
          navItem: "debts",
        },
        {
          label: "Payments",
          value: payments.length,
          hint: `${formatMoney(totalPayments)} collected overall`,
          icon: <MdOutlinePayments />,
          navItem: "payments",
        },
      ],
      cashier: [
        {
          label: "Payments",
          value: payments.length,
          hint: `${formatMoney(totalPayments)} collected overall`,
          icon: <MdOutlinePayments />,
          navItem: "payments",
        },
        {
          label: "Active Debts",
          value: activeDebts.length,
          hint: `${formatMoney(totalDebtBalance)} outstanding balance`,
          icon: <RiSecurePaymentLine />,
          navItem: "debts",
        },
        {
          label: "Collected Today",
          value: formatMoney(todayCollectedTotal),
          hint: `${todaysPayments.length} payment(s) posted today`,
          icon: <MdReceiptLong />,
          navItem: "payments",
        },
        {
          label: "Due Today",
          value: todaysDueDebts.length,
          hint: `${formatMoney(todayOutstandingBalance)} still outstanding`,
          icon: <MdCalendarToday />,
          navItem: "debts",
        },
      ],
      collections_officer: [
        {
          label: "Assigned Loans",
          value: dashboardDebts.length,
          hint: "Loan accounts currently assigned to you",
          icon: <RiSecurePaymentLine />,
          navItem: "debts",
        },
        {
          label: "Active Loans",
          value: activeDebts.length,
          hint: `${formatMoney(totalDebtBalance)} outstanding balance`,
          icon: <MdOutlinePayments />,
          navItem: "debts",
        },
        {
          label: "Due Today",
          value: todaysDueDebts.length,
          hint: `${formatMoney(todayOutstandingBalance)} due across assigned loans`,
          icon: <MdCalendarToday />,
          navItem: "debts",
        },
        {
          label: "Collected on Loans",
          value: formatMoney(totalPaidAcrossDebts),
          hint: "Repayments posted across your assigned loan accounts",
          icon: <MdReceiptLong />,
          navItem: "debts",
        },
      ],
    };

    const todayCards = isLoanOfficer(currentUser)
      ? [
          {
            label: "Assigned Due Today",
            value: formatMoney(todayDueDebtTotal),
            hint: `${todaysDueDebts.length} assigned debt record(s) due today`,
          },
          {
            label: "Active Assigned Loans",
            value: activeDebts.length,
            hint: "Assigned loan accounts still in repayment",
          },
          {
            label: "Outstanding Today",
            value: formatMoney(todayOutstandingBalance),
            hint: "Remaining balance on your assigned loans due today",
          },
        ]
      : [
          {
            label: "Due Debt Today",
            value: formatMoney(todayDueDebtTotal),
            hint: `${todaysDueDebts.length} debt record(s) due today`,
          },
          {
            label: "Collected Today",
            value: formatMoney(todayCollectedTotal),
            hint: `${todaysPayments.length} payment(s) posted today`,
          },
          {
            label: "Outstanding Today",
            value: formatMoney(todayOutstandingBalance),
            hint: "Remaining balance on today-due debts",
          },
        ];

    const quickLinksByRole = {
      admin: [
        {
          title: "Record a sale",
          text: "Create and track new motorcycle purchases across cash and loan workflows.",
          action: "Sales",
          navItem: "sales",
          icon: <MdReceiptLong />,
        },
        {
          title: "Manage customers",
          text: "Review customer records, loan activity, and contact details in one place.",
          action: "Customers",
          navItem: "customers",
          icon: <FaUsers />,
        },
      ],
      cashier: [
        {
          title: "Post a payment",
          text: "Open the payments workspace and record incoming customer repayments.",
          action: "Payments",
          navItem: "payments",
          icon: <MdOutlinePayments />,
        },
        {
          title: "Review due debts",
          text: "Check balances and identify accounts that need collection attention today.",
          action: "Debts",
          navItem: "debts",
          icon: <RiSecurePaymentLine />,
        },
      ],
      collections_officer: [
        {
          title: "Review assigned loans",
          text: "Focus on the loan accounts already assigned to you for repayment follow-up.",
          action: "Assigned debts",
          navItem: "debts",
          icon: <RiSecurePaymentLine />,
        },
      ],
    };

    const summaryCards = summaryCardsByRole[currentRole] || summaryCardsByRole.admin;
    const quickLinks = quickLinksByRole[currentRole] || quickLinksByRole.admin;
    const roleHeading = formatAdminRole(currentRole);
    const showPortfolioInsights = currentRole === "admin";
    const showPaymentsDailyCard = canManagePayments(currentUser) || isLoanOfficer(currentUser);

    return (
      <div className="container">
        <div className="app-page">
          <div className="app-page-hero">
            <div>
              <h6 className="main-section-title">{roleHeading} dashboard</h6>
              <p className="app-page-subtitle">
                {currentRole === "admin"
                  ? "Track portfolio-wide performance and today's collections from one organized admin workspace."
                  : currentRole === "cashier"
                    ? "Stay focused on payment posting, balances, and today&apos;s debt collection workload."
                    : "Monitor only the loan accounts assigned to you and keep follow-up priorities clear."}
              </p>
            </div>
          </div>

          <div className="row">
            {summaryCards.map((item) => (
              <div className="col-md-6 col-xl-3" key={item.label}>
                <div
                  className="dashboard-stat-card h-100"
                  onClick={() => {
                    appContext.navTo({ item: item.navItem });
                  }}
                >
                  <div className="dashboard-stat-icon">{item.icon}</div>
                  <div className="dashboard-stat-value">{item.value}</div>
                  <div className="dashboard-stat-label">{item.label}</div>
                  <div className="dashboard-stat-hint">{item.hint}</div>
                </div>
              </div>
            ))}
          </div>

          {showPaymentsDailyCard ? (
            <div className="row mt-3">
              <div className="col-12">
                <div className="insight-card px-3 pb-4">
                  <div className="insight-card-header">
                    <div>
                      <h6 className="section-title">Daily debt tracking</h6>
                    </div>

                    <span className="status-pill status-pill-primary">
                      <MdCalendarToday size={14} /> {todayLabel}
                    </span>
                  </div>

                  <div className="row">
                    {todayCards.map((item) => (
                      <div className="col-md-4 mt-3" key={item.label}>
                        <div className="app-form-summary-card h-100">
                          <span>{item.label}</span>
                          <strong>{item.value}</strong>
                          <small className="text-muted">{item.hint}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="row">
            <div className="col-md-12">
              <div className="insight-card insight-card-primary px-3 pb-4">
                <div className="insight-card-header">
                  <div>
                    <h6 className="section-title">{showPortfolioInsights ? "General records" : "Collection snapshot"}</h6>
                  </div>
                </div>

                <div className="insight-grid">
                  {showPortfolioInsights ? (
                    <>
                      <div className="insight-metric">
                        <span className="insight-metric-label">Outstanding balance</span>
                        <strong>{formatMoney(totalDebtBalance)}</strong>
                      </div>
                      <div className="insight-metric">
                        <span className="insight-metric-label">Total debt issued</span>
                        <strong>{formatMoney(totalDebtAmount)}</strong>
                      </div>
                      <div className="insight-metric">
                        <span className="insight-metric-label">Collected on debts</span>
                        <strong>{formatMoney(totalPaidAcrossDebts)}</strong>
                      </div>
                      <div className="insight-metric">
                        <span className="insight-metric-label">Recorded sales</span>
                        <strong>{sales.length}</strong>
                      </div>
                      <div className="insight-metric d-none">
                        <span className="insight-metric-label">Sales created today</span>
                        <strong>{todaysSales.length}</strong>
                      </div>
                      <div className="insight-metric">
                        <span className="insight-metric-label">Active loan accounts</span>
                        <strong>{activeDebts.length}</strong>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="insight-metric">
                        <span className="insight-metric-label">Outstanding balance</span>
                        <strong>{formatMoney(totalDebtBalance)}</strong>
                      </div>
                      <div className="insight-metric">
                        <span className="insight-metric-label">Collected on debts</span>
                        <strong>{formatMoney(totalPaidAcrossDebts)}</strong>
                      </div>
                      <div className="insight-metric">
                        <span className="insight-metric-label">Payments posted</span>
                        <strong>{payments.length}</strong>
                      </div>
                      <div className="insight-metric">
                        <span className="insight-metric-label">Payments today</span>
                        <strong>{todaysPayments.length}</strong>
                      </div>
                      <div className="insight-metric">
                        <span className="insight-metric-label">Active loan accounts</span>
                        <strong>{activeDebts.length}</strong>
                      </div>
                      <div className="insight-metric">
                        <span className="insight-metric-label">Accounts due today</span>
                        <strong>{todaysDueDebts.length}</strong>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }

  return <div className="container mSupportLoading">{getInlineLoader()}</div>;
}
